import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { pushService, QuestionnairePush, QuestionnairePushRequest } from '../services/pushService';
import { questionnaireService } from '../services/questionnaireService';
import { schoolService } from '../services/schoolService';
import { classService } from '../services/classService';
import { Questionnaire, School } from '../types';
import { ArrowLeftIcon, PlusIcon } from '../components/Icons';

const GRADE_DISPLAY_MAP: Record<string, string> = {
  '七年级': '七年级（初一）',
  '八年级': '八年级（初二）',
  '九年级': '九年级（初三）',
};

const getGradeDisplay = (grade: string) => GRADE_DISPLAY_MAP[grade] || grade;

// 可选的角色列表
const AVAILABLE_ROLES = [
  { value: 'STUDENT', label: '学生' },
  { value: 'PARENT', label: '家长' },
  { value: 'TEACHER', label: '教师' },
];

const QuestionnairePushPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const openCreate = (location.state as any)?.openCreate === true;
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [pushes, setPushes] = useState<QuestionnairePush[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [availableGrades, setAvailableGrades] = useState<string[]>([]);
  const [availableAcademicYears, setAvailableAcademicYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(openCreate);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPush, setSelectedPush] = useState<QuestionnairePush | null>(null);
  const [formData, setFormData] = useState<QuestionnairePushRequest>({
    questionnaireId: Number(id),
    targetRoles: ['STUDENT']
  });
  const needGrades = formData.targetRoles.includes('STUDENT') || formData.targetRoles.includes('PARENT');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionnaireData, pushesData, schoolsData, gradesData, academicYearsData] = await Promise.all([
        questionnaireService.getQuestionnaire(Number(id)),
        pushService.getQuestionnairePushes(Number(id)),
        schoolService.getSchools({ page: 0, size: 100 }),
        classService.getAllGrades(),
        classService.getAllAcademicYears()
      ]);
      setQuestionnaire(questionnaireData);
      setPushes(pushesData);
      setSchools(schoolsData.content);
      setAvailableGrades(gradesData);
      setAvailableAcademicYears(academicYearsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (formData.targetRoles.length !== 1) {
      alert('请选择一个目标角色');
      return;
    }

    try {
      await pushService.createPush(formData);
      setShowCreateModal(false);
      setFormData({
        questionnaireId: Number(id),
        targetRoles: ['STUDENT']
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create push:', error);
      alert('创建推送失败');
    }
  };

  const handleViewDetail = (push: QuestionnairePush) => {
    setSelectedPush(push);
    setShowDetailModal(true);
  };

  const handleDelete = async (pushId: number) => {
    if (!window.confirm('确定要删除这个推送吗？')) return;

    try {
      await pushService.deletePush(pushId);
      fetchData();
    } catch (error) {
      console.error('Failed to delete push:', error);
      alert('删除失败');
    }
  };

  const handleSelectRole = (role: string) => {
    const isGradeRole = role === 'STUDENT' || role === 'PARENT';
    setFormData(prev => ({
      ...prev,
      targetRoles: [role],
      ...(!isGradeRole && { grades: undefined, academicYears: undefined }),
    }));
  };

  const handleToggleGrade = (grade: string) => {
    setFormData(prev => ({
      ...prev,
      grades: prev.grades
        ? prev.grades.includes(grade)
          ? prev.grades.filter(g => g !== grade)
          : [...prev.grades, grade]
        : [grade]
    }));
  };

  const handleToggleAcademicYear = (year: string) => {
    setFormData(prev => ({
      ...prev,
      academicYears: prev.academicYears
        ? prev.academicYears.includes(year)
          ? prev.academicYears.filter(y => y !== year)
          : [...prev.academicYears, year]
        : [year]
    }));
  };

  const handleToggleSchool = (schoolId: number) => {
    setFormData(prev => ({
      ...prev,
      schoolIds: prev.schoolIds
        ? prev.schoolIds.includes(schoolId)
          ? prev.schoolIds.filter(id => id !== schoolId)
          : [...prev.schoolIds, schoolId]
        : [schoolId]
    }));
  };

  if (loading || !questionnaire) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <button
        onClick={() => navigate(`/questionnaires/${id}`)}
        className="flex items-center text-on-surface-variant hover:text-on-surface mb-4"
      >
        <ArrowLeftIcon size={20} className="mr-2" />
        返回问卷详情
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-on-surface mb-2 font-headline">问卷推送管理</h1>
        <p className="text-on-surface-variant font-body">{questionnaire.title}</p>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-on-surface font-headline">推送列表</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl hover:opacity-90 transition-colors shadow-teal"
        >
          <PlusIcon size={20} />
          创建推送
        </button>
      </div>

      {pushes.length === 0 ? (
        <div className="text-center py-12 bg-surface-container-lowest rounded-3xl shadow-soft">
          <p className="text-on-surface-variant mb-4 font-body">暂无推送记录</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 gradient-primary text-on-primary rounded-xl hover:opacity-90 transition-colors shadow-teal"
          >
            <PlusIcon size={20} />
            创建推送
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {pushes.map((push) => (
            <div
              key={push.id}
              className="bg-surface-container-lowest p-6 rounded-2xl shadow-soft cursor-pointer hover:shadow-soft-md transition-shadow"
              onClick={() => handleViewDetail(push)}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-xl text-xs font-medium font-label">
                      {push.statusDisplayName}
                    </span>
                    <span className="text-sm text-on-surface-variant font-label">
                      创建者：{push.createdByUsername}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm font-body">
                    <div>
                      <span className="text-on-surface-variant font-label">目标角色：</span>
                      <span className="font-medium">
                        {push.targetRoles.map(r => getRoleDisplayName(r)).join(', ')}
                      </span>
                    </div>
                    {push.schoolIds && push.schoolIds.length > 0 && (
                      <div>
                        <span className="text-on-surface-variant font-label">学校：</span>
                        <span className="font-medium">{push.schoolIds.length} 所</span>
                      </div>
                    )}
                    {push.academicYears && push.academicYears.length > 0 && (
                      <div>
                        <span className="text-on-surface-variant font-label">学年：</span>
                        <span className="font-medium">{push.academicYears.join(', ')}</span>
                      </div>
                    )}
                    {push.grades && push.grades.length > 0 && (
                      <div>
                        <span className="text-on-surface-variant font-label">年级：</span>
                        <span className="font-medium">{push.grades.map(g => getGradeDisplay(g)).join(', ')}</span>
                      </div>
                    )}
                    {push.startTime && (
                      <div>
                        <span className="text-on-surface-variant font-label">开始时间：</span>
                        <span className="font-medium">
                          {new Date(push.startTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {push.endTime && (
                      <div>
                        <span className="text-on-surface-variant font-label">结束时间：</span>
                        <span className="font-medium">
                          {new Date(push.endTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(push.id);
                  }}
                  className="ml-4 text-danger-600 hover:text-danger-700 text-sm font-medium"
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 创建推送浮窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <h2 className="text-xl font-bold text-on-surface mb-4 font-headline">创建问卷推送</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                  目标角色 *
                </label>
                <div className="flex gap-2 flex-wrap">
                  {AVAILABLE_ROLES.map(role => (
                    <button
                      key={role.value}
                      onClick={() => handleSelectRole(role.value)}
                      className={`px-4 py-2 rounded-xl ${
                        formData.targetRoles.includes(role.value)
                          ? 'gradient-primary text-on-primary shadow-teal'
                          : 'bg-surface-container-high text-on-surface hover:bg-surface-container-low'
                      }`}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                  推送到学校（可选，不选表示所有学校）
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto bg-surface-container-high rounded-xl p-2">
                  {schools.map(school => (
                    <label key={school.id} className="flex items-center gap-2 p-2 hover:bg-surface-container-low rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.schoolIds?.includes(school.id) || false}
                        onChange={() => handleToggleSchool(school.id)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-body">{school.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 学年选择仅在选了学生或家长角色时显示 */}
              {needGrades && (
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                  推送学年（可选，不选表示所有学年）
                </label>
                <div className="grid grid-cols-4 gap-2 bg-surface-container-high rounded-xl p-3">
                  {availableAcademicYears.length > 0 ? availableAcademicYears.map(year => (
                    <label key={year} className="flex items-center gap-2 p-2 hover:bg-surface-container-low rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.academicYears?.includes(year) || false}
                        onChange={() => handleToggleAcademicYear(year)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-body">{year}</span>
                    </label>
                  )) : (
                    <p className="text-sm text-on-surface-variant col-span-4 py-2 font-body">暂无学年数据，请先创建班级</p>
                  )}
                </div>
              </div>
              )}

              {/* 年级选择仅在选了学生或家长角色时显示，教师/管理员不按年级分 */}
              {needGrades && (
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                  推送到年级（可选，不选表示所有年级）
                </label>
                <div className="grid grid-cols-3 gap-2 bg-surface-container-high rounded-xl p-3">
                  {availableGrades.length > 0 ? availableGrades.map(grade => (
                    <label key={grade} className="flex items-center gap-2 p-2 hover:bg-surface-container-low rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.grades?.includes(grade) || false}
                        onChange={() => handleToggleGrade(grade)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-body">{getGradeDisplay(grade)}</span>
                    </label>
                  )) : (
                    <p className="text-sm text-on-surface-variant col-span-3 py-2 font-body">暂无年级数据，请先创建班级</p>
                  )}
                </div>
              </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                    开始时间（可选）
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    onChange={(e) => setFormData({
                      ...formData,
                      startTime: e.target.value ? new Date(e.target.value).toISOString() : undefined
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface mb-2 font-label">
                    结束时间（可选）
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                    onChange={(e) => setFormData({
                      ...formData,
                      endTime: e.target.value ? new Date(e.target.value).toISOString() : undefined
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({
                    questionnaireId: Number(id),
                    targetRoles: ['STUDENT']
                  });
                }}
                className="flex-1 px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low"
              >
                取消
              </button>
              <button
                onClick={handleCreate}
                className="flex-1 px-4 py-2 gradient-primary text-on-primary rounded-xl hover:opacity-90 shadow-teal"
              >
                创建推送
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 推送详情弹窗 */}
      {showDetailModal && selectedPush && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-container-lowest rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-on-surface font-headline">推送详情</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-on-surface-variant hover:text-on-surface"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }} className="pb-4">
                <div className="grid grid-cols-2 gap-4 text-sm font-body">
                  <div>
                    <span className="text-on-surface-variant font-label">推送状态：</span>
                    <span className="font-medium">{selectedPush.statusDisplayName}</span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant font-label">创建者：</span>
                    <span className="font-medium">{selectedPush.createdByUsername}</span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant font-label">创建时间：</span>
                    <span className="font-medium">
                      {new Date(selectedPush.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-on-surface-variant font-label">问卷标题：</span>
                    <span className="font-medium">{selectedPush.questionnaireTitle}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-on-surface mb-2 font-headline">推送范围</h3>
                <div className="bg-surface-container-low p-4 rounded-2xl space-y-2 text-sm font-body">
                  <div>
                    <span className="text-on-surface-variant font-label">目标角色：</span>
                    <span className="font-medium ml-2">
                      {selectedPush.targetRoles.map(r => getRoleDisplayName(r)).join(', ')}
                    </span>
                  </div>
                  {selectedPush.schoolNames && selectedPush.schoolNames.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">学校：</span>
                      <span className="font-medium ml-2">{selectedPush.schoolNames.join(', ')}</span>
                    </div>
                  )}
                  {selectedPush.academicYears && selectedPush.academicYears.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">学年：</span>
                      <span className="font-medium ml-2">{selectedPush.academicYears.join(', ')}</span>
                    </div>
                  )}
                  {selectedPush.grades && selectedPush.grades.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">年级：</span>
                      <span className="font-medium ml-2">{selectedPush.grades.map(g => getGradeDisplay(g)).join(', ')}</span>
                    </div>
                  )}
                  {selectedPush.classNames && selectedPush.classNames.length > 0 && (
                    <div>
                      <span className="text-on-surface-variant font-label">班级：</span>
                      <span className="font-medium ml-2">{selectedPush.classNames.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>

              {(selectedPush.startTime || selectedPush.endTime) && (
                <div>
                  <h3 className="font-medium text-on-surface mb-2 font-headline">时间范围</h3>
                  <div className="bg-surface-container-low p-4 rounded-2xl space-y-2 text-sm font-body">
                    {selectedPush.startTime && (
                      <div>
                        <span className="text-on-surface-variant font-label">开始时间：</span>
                        <span className="font-medium ml-2">
                          {new Date(selectedPush.startTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedPush.endTime && (
                      <div>
                        <span className="text-on-surface-variant font-label">结束时间：</span>
                        <span className="font-medium ml-2">
                          {new Date(selectedPush.endTime).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function getRoleDisplayName(role: string): string {
  const roleMap: Record<string, string> = {
    'STUDENT': '学生',
    'TEACHER': '教师',
    'PARENT': '家长',
    'ADMIN': '管理员',
    'USER': '用户'
  };
  return roleMap[role] || role;
}

export default QuestionnairePushPage;
